import Backdrop from '@atoms/Backdrop/Backdrop';
import CloseButton from '@atoms/CloseButton/CloseButton';
import Portal from '@atoms/Portal/Portal';
import bodyScrollLock from '@utils/helpers/bodyScrollLock';
import classNames from 'classnames';
import FocusTrap from 'focus-trap-react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CSSProperties,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

export interface IDrawerContentRef {
  readonly scrollToTop: () => void;
}

interface Props {
  readonly backgroundColourClassName?: string;
  readonly closeButtonClassNames?: string;
  readonly closeButtonPositionClassName?: string;
  readonly contentClassName?: string;
  readonly contentSpacingClassName?: string;
  readonly dataTestId?: string;
  readonly drawerClasses?: string;
  readonly isBottomToTopSliding?: boolean;
  readonly isOpen: boolean;
  readonly maxWidthClassName?: string;
  readonly onCloseCallback?: () => void;
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
  readonly showClose?: boolean;
  readonly style?: CSSProperties;
}

const Drawer = forwardRef<IDrawerContentRef, PropsWithChildren<Props>>(
  (
    {
      backgroundColourClassName = 'bg-grey-100',
      children,
      closeButtonClassNames,
      closeButtonPositionClassName = 'top-4 right-4',
      contentClassName,
      contentSpacingClassName = 'pt-14',
      dataTestId = 'Drawer',
      drawerClasses,
      isBottomToTopSliding,
      isOpen,
      maxWidthClassName = 'max-w-[605px]',
      onCloseCallback,
      setIsOpen,
      showClose = true,
      style,
    },
    contentRef
  ) => {
    const drawerRef = useRef<HTMLDivElement>(null);
    const drawerContentRef = useRef<HTMLDivElement>(null);
    const iframeHeightRef = useRef<string | null>(null);

    const isInEditor =
      typeof window !== 'undefined' && window.top !== window.self;

    const variants = {
      hidden: {
        ...(isBottomToTopSliding ? { y: '100vh' } : { x: '100vw' }),
        transition: { duration: 0.5 },
      },
      show: {
        ...(isBottomToTopSliding ? { y: 0 } : { x: 0 }),
        transition: { duration: 0.5 },
      },
    };

    const handleClose = () => {
      setIsOpen(false);
      onCloseCallback?.();
    };

    useImperativeHandle(contentRef, () => ({
      scrollToTop: () => {
        drawerContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      },
    }));

    useEffect(() => {
      if (isOpen) {
        bodyScrollLock(true);
      }

      return () => {
        bodyScrollLock(false);
      };
    }, [isOpen]);

    useEffect(() => {
      const close = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      };

      window.addEventListener('keydown', close);
      return () => window.removeEventListener('keydown', close);
    }, [setIsOpen]);

    useEffect(() => {
      if (!isInEditor) return;

      const editorIFrame = drawerRef.current?.ownerDocument.defaultView
        ?.frameElement as HTMLIFrameElement;

      if (!editorIFrame) return;

      if (!iframeHeightRef.current) {
        iframeHeightRef.current = editorIFrame.style.height;
      }

      editorIFrame.style.height = isOpen ? '100vh' : iframeHeightRef.current;
    }, [isInEditor, isOpen]);

    return (
      <AnimatePresence>
        {isOpen && (
          <Portal key="portal" mountId="drawer-root">
            <FocusTrap
              focusTrapOptions={{
                preventScroll: true,
              }}
            >
              <div ref={drawerRef} data-testid={dataTestId}>
                <Backdrop
                  className="relative z-max"
                  key="backdrop"
                  onCloseCallback={handleClose}
                />

                <motion.div
                  key="sidebar"
                  onClick={(e) => e.stopPropagation()}
                  className={classNames(
                    'drawer fixed inset-0 left-auto z-max flex w-full flex-col overflow-hidden',
                    backgroundColourClassName,
                    maxWidthClassName,
                    drawerClasses
                  )}
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  variants={variants}
                  data-testid="drawer"
                >
                  <div
                    ref={drawerContentRef}
                    className={classNames(
                      'relative flex h-full flex-col',
                      contentSpacingClassName,
                      contentClassName
                    )}
                    style={style}
                  >
                    {/* A hidden button that steals the inital focus of the modal preventing the close icon from animating on modal open  */}
                    <button className="hidden" aria-hidden="true" />
                    {children}
                    {showClose && (
                      <CloseButton
                        onClick={handleClose}
                        className={classNames(
                          'absolute cursor-pointer text-sm before:h-[2px] before:w-4 after:h-[2px] after:w-4',
                          closeButtonClassNames,
                          closeButtonPositionClassName
                        )}
                        size="2rem"
                      />
                    )}
                  </div>
                </motion.div>
              </div>
            </FocusTrap>
          </Portal>
        )}
      </AnimatePresence>
    );
  }
);

export default Drawer;
